import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "../axios/AxiosInstance";
import {
  UserCog,
  UserCircle,
  ShieldCheck,
  Save,
  Mail,
  Phone,
  User,
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Settings,
  Bell,
  Palette
} from "lucide-react";

export default function MonComptePage() {
  // États pour les informations utilisateur
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [mail, setMail] = useState("");
  const [phone, setPhone] = useState("");

  // États pour la sécurité
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // États pour les préférences
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [darkModeEnabled, setDarkModeEnabled] = useState(false);

  // États pour l'interface
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");



  // Fonction pour formater le numéro de téléphone au format international
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return "";

    // Supprimer tous les espaces, tirets et autres caractères non numériques
    let cleaned = phoneNumber.replace(/\D/g, '');

    // Si le numéro commence par 0, le remplacer par +33
    if (cleaned.startsWith('0')) {
      cleaned = '+33' + cleaned.substring(1);
    }
    // Si le numéro ne commence pas par +, ajouter +33
    else if (!cleaned.startsWith('33') && !phoneNumber.startsWith('+')) {
      cleaned = '+33' + cleaned;
    }
    // Si le numéro commence par 33, ajouter le +
    else if (cleaned.startsWith('33') && !phoneNumber.startsWith('+')) {
      cleaned = '+' + cleaned;
    }

    return cleaned;
  };

  // Fonction pour mettre à jour le téléphone
  const updatePhone = async () => {
    console.log("updatePhone appelé avec:", phone);

    if (!phone || phone.trim() === "") {
      toast.error("Veuillez saisir un numéro de téléphone");
      return;
    }

    // Formater le numéro au format international
    const formattedPhone = formatPhoneNumber(phone);
    console.log("Numéro formaté:", formattedPhone);

    setIsLoading(true);
    try {
      console.log("Envoi de la requête PATCH vers user");
      const response = await axios.patch("user", { phone: formattedPhone });
      console.log("Réponse du serveur:", response.data);

      // Mettre à jour le state et localStorage avec le numéro formaté
      setPhone(formattedPhone);
      localStorage.setItem("phone", formattedPhone);
      toast.success("Numéro de téléphone modifié avec succès !");
    } catch (error) {
      console.error("Erreur lors de la modification du téléphone:", error);

      if (error.response) {
        // Erreur de réponse du serveur
        console.error("Statut:", error.response.status);
        console.error("Données:", error.response.data);

        // Afficher le message d'erreur spécifique du serveur
        const errorMessage = error.response.data?.message;
        if (Array.isArray(errorMessage)) {
          toast.error(`Erreur: ${errorMessage.join(', ')}`);
        } else {
          toast.error(`Erreur: ${errorMessage || 'Format de téléphone invalide'}`);
        }
      } else if (error.request) {
        // Erreur de réseau
        console.error("Erreur réseau:", error.request);
        toast.error("Erreur de connexion au serveur");
      } else {
        // Autre erreur
        console.error("Erreur:", error.message);
        toast.error("Erreur lors de la modification du téléphone");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction pour mettre à jour le mot de passe
  const updatePassword = async () => {
    if (!password || !newPassword || !confirmPassword) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Les nouveaux mots de passe ne correspondent pas");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Le nouveau mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setIsLoading(true);
    try {
      await axios.post("auth/update-password", { password, newPassword });
      toast.success("Mot de passe modifié avec succès !");
      setPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la modification du mot de passe");
    } finally {
      setIsLoading(false);
    }
  };

  // Charger les données utilisateur
  useEffect(() => {
    const firstName = localStorage.getItem("firstname");
    setFirstName(firstName || "");
    const lastName = localStorage.getItem("lastname");
    setLastName(lastName || "");
    const mail = localStorage.getItem("userEmail");
    setMail(mail || "");
    const phone = localStorage.getItem("phone");
    setPhone(phone || "");
  }, []);

  return (
    <div className="w-full min-h-screen">
      <div className="w-full p-8">
        {/* Header Section */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <UserCog className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-slate-900">
                  Mon Compte
                </h1>
                <p className="text-lg text-slate-600">
                  Gérez vos informations personnelles et préférences
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Navigation par onglets */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-1">
            <div className="flex space-x-1">
              {[
                { id: "profile", label: "Profil", icon: UserCircle },
                { id: "security", label: "Sécurité", icon: ShieldCheck },
                { id: "preferences", label: "Préférences", icon: Settings }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-all duration-200 ${
                    activeTab === tab.id
                      ? "bg-blue-600 text-white shadow-lg"
                      : "text-slate-600 hover:text-slate-800 hover:bg-slate-100"
                  }`}
                >
                  <tab.icon size={18} />
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Contenu des onglets */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {activeTab === "profile" && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
              <div className="flex items-center space-x-3 mb-6">
                <UserCircle className="w-6 h-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-900">Informations du Profil</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Prénom */}
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm font-medium text-slate-700">
                    <User size={16} />
                    <span>Prénom</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={firstName}
                      disabled
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-medium capitalize cursor-not-allowed"
                      placeholder="Votre prénom"
                    />
                  </div>
                </div>

                {/* Nom */}
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm font-medium text-slate-700">
                    <User size={16} />
                    <span>Nom</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={lastName}
                      disabled
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-medium capitalize cursor-not-allowed"
                      placeholder="Votre nom"
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm font-medium text-slate-700">
                    <Mail size={16} />
                    <span>Adresse Email</span>
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      value={mail}
                      disabled
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-medium cursor-not-allowed"
                      placeholder="votre@email.com"
                    />
                  </div>
                </div>

                {/* Téléphone */}
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm font-medium text-slate-700">
                    <Phone size={16} />
                    <span>Téléphone</span>
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="flex-1 px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="06 12 34 56 78 ou +33 6 12 34 56 78"
                    />
                    <button
                      onClick={updatePhone}
                      disabled={isLoading}
                      className="px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      <Save size={16} />
                      <span>Sauvegarder</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <div className="flex items-center space-x-2 text-blue-800">
                  <AlertCircle size={16} />
                  <span className="text-sm font-medium">Information</span>
                </div>
                <p className="text-sm text-blue-700 mt-1">
                  Pour modifier vos informations personnelles, veuillez contacter votre agence.
                </p>
              </div>
            </div>
          )}

          {activeTab === "security" && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
              <div className="flex items-center space-x-3 mb-6">
                <ShieldCheck className="w-6 h-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-900">Sécurité</h2>
              </div>

              <div className="space-y-6">
                {/* Mot de passe actuel */}
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm font-medium text-slate-700">
                    <Lock size={16} />
                    <span>Mot de passe actuel</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-3 pr-12 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Nouveau mot de passe */}
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm font-medium text-slate-700">
                    <Lock size={16} />
                    <span>Nouveau mot de passe</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-4 py-3 pr-12 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Confirmer le mot de passe */}
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm font-medium text-slate-700">
                    <CheckCircle size={16} />
                    <span>Confirmer le nouveau mot de passe</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-4 py-3 pr-12 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Bouton de mise à jour */}
                <button
                  onClick={updatePassword}
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-6 rounded-xl font-medium hover:from-red-600 hover:to-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Save size={18} />
                      <span>Changer le mot de passe</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {activeTab === "preferences" && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
              <div className="flex items-center space-x-3 mb-6">
                <Settings className="w-6 h-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-900">Préférences</h2>
              </div>

              <div className="space-y-6">
                {/* Notifications */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Bell size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-800">Notifications push</h3>
                      <p className="text-sm text-slate-600">Recevoir des notifications dans l'application</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificationsEnabled}
                      onChange={(e) => setNotificationsEnabled(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                {/* Notifications email */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Mail size={20} className="text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-800">Notifications email</h3>
                      <p className="text-sm text-slate-600">Recevoir des emails de notification</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={emailNotifications}
                      onChange={(e) => setEmailNotifications(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                  </label>
                </div>

                {/* Mode sombre */}
               {/*  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <Palette size={20} className="text-slate-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-800">Mode sombre</h3>
                      <p className="text-sm text-slate-600">Activer le thème sombre (bientôt disponible)</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-not-allowed opacity-50">
                    <input
                      type="checkbox"
                      checked={darkModeEnabled}
                      disabled
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-600"></div>
                  </label>
                </div> */}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
